import get from 'lodash/get';
import { createReducerContext } from '../../utils/createReducerContext';
import { CONVERSION_STATUS } from '../AssignmentSubmission/AssignmentAttachmentContext';
import { buildSubmissionListFromGroupedSubmissions } from '../../helpers/buildSubmissionListFromGroupedSubmissions';

const initialState = {
  teacherFeedback: '',
  studentScore: null,
  assignment: null,
  submittedStudents: [],
  selectedStudentIndex: null,
  studentsCount: 0,
  selectedStudent: null,
  isFileUploadConfirmationOpen: false,
  isReturnSubmissionConfirmationOpen: false,
  isUnsavedChangesSubmissionConfirmationOpen: false,
  unsavedChangesConfirmationPayload: null,
  isSaveButtonEnabled: false,
  isRequestResubmissionModalOpen: false,
  reRequestedStudentId: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setFeedback':
      return { ...state, teacherFeedback: action.payload };
    case 'setupAssignmentData': {
      let submittedStudents = buildSubmissionListFromGroupedSubmissions(
        action.payload.groupedSubmissions,
      ).sort(({ student: studentA }, { student: studentB }) =>
        studentA.name.localeCompare(studentB.name),
      );

      if (
        action.payload.assignment.commentRequired ||
        action.payload.assignment.attachmentRequired
      ) {
        submittedStudents = submittedStudents.filter(
          s => s.submission && s.submission.submittedOn,
        );
      }

      const selectedStudentIndex = submittedStudents.findIndex(
        s => s.student.id === action.payload.selectedStudentId,
      );
      const selectedStudent = submittedStudents[selectedStudentIndex];
      return {
        ...state,
        submittedStudents,
        selectedStudentIndex,
        selectedStudent,
        studentsCount: submittedStudents.length,
        studentScore:
          selectedStudent.submission && selectedStudent.submission.response
            ? selectedStudent.submission.response.score
            : null,
        assignment: action.payload.assignment,
        teacherFeedback:
          selectedStudent.submission && selectedStudent.submission.response
            ? selectedStudent.submission.response.comment
            : '',
      };
    }
    case 'score':
      return { ...state, studentScore: action.payload };
    case 'openFileUploadConfirmationModal':
      return { ...state, isFileUploadConfirmationOpen: true };
    case 'closeFileUploadConfirmationModal':
      return { ...state, isFileUploadConfirmationOpen: false };
    case 'openReturnSubmissionConfirmation':
      return { ...state, isReturnSubmissionConfirmationOpen: true };
    case 'closeReturnSubmissionConfirmation':
      return { ...state, isReturnSubmissionConfirmationOpen: false };
    case 'openUnsavedChangesSubmissionConfirmation':
      return {
        ...state,
        isUnsavedChangesSubmissionConfirmationOpen: true,
        unsavedChangesConfirmationPayload: action.payload,
      };
    case 'closeUnsavedChangesSubmissionConfirmation':
      return { ...state, isUnsavedChangesSubmissionConfirmationOpen: false };
    case 'calculateSaveButtonStatus': {
      const selectedStudentSubmissionResponse = get(
        state.selectedStudent,
        'submission.response',
      );
      const responseAttachment = get(
        state.selectedStudent,
        'submission.response.attachment',
      );
      const submissionAttachment = get(
        state.selectedStudent,
        'submission.attachment',
      );
      const {
        isReturningInProgress,
        isReturned,
        isGradeFieldFocused,
        conversionStatus,
        attachment,
      } = action.payload;
      const isSaveButtonEnabled =
        !isReturningInProgress &&
        !!(state.selectedStudent && state.assignment) &&
        !isReturned &&
        !isGradeFieldFocused &&
        conversionStatus !== CONVERSION_STATUS.inProgress &&
        ((selectedStudentSubmissionResponse &&
          state.studentScore !== selectedStudentSubmissionResponse.score) ||
          (selectedStudentSubmissionResponse &&
            state.teacherFeedback !==
              selectedStudentSubmissionResponse.comment) ||
          (!selectedStudentSubmissionResponse &&
            Number.isInteger(state.studentScore)) ||
          (!selectedStudentSubmissionResponse && state.teacherFeedback) ||
          (selectedStudentSubmissionResponse &&
            responseAttachment &&
            attachment &&
            responseAttachment.path !== attachment.path) ||
          (submissionAttachment &&
            !responseAttachment &&
            attachment &&
            attachment.path !== submissionAttachment.path));

      return { ...state, isSaveButtonEnabled };
    }
    case 'restoreDefaults':
      return { ...initialState };
    case 'openRequestResubmissionModal': {
      const { studentId } = action.payload;
      return {
        ...state,
        isRequestResubmissionModalOpen: true,
        reRequestedStudentId: studentId,
      };
    }
    case 'closeRequestResubmissionModal':
      return {
        ...state,
        isRequestResubmissionModalOpen: false,
        reRequestedStudentId: '',
      };
    default: {
      console.error(`Unknown action type: ${action.type} was dispatched`);
      return state;
    }
  }
};

export const {
  ReducerProvider: StudentSubmissionProvider,
  useReducerDispatch: useStudentSubmissionDispatch,
  useReducerState: useStudentSubmissionState,
} = createReducerContext('StudentSubmission', reducer, initialState);
